/* eslint-disable valid-jsdoc */
import { useCallback, useEffect, useState } from 'react';

import {
  type IUseStructuredLogParams,
  type IStructuredLogBase,
  type IStructuredLogConfig,
} from '../types/structured-log-types';
import { structuredLogEventName } from '../utils/dispatch-structured-log-event';
import { handleStructuredLogEvent } from '../utils/handle-structured-log-event';

/**
 * `useStructuredLog` hooks listens for structured log events
 * * @param params - parameters to handle the structured log event
 * * @param params.activity - activity to handle the structured log event
 * * @param params.botServiceConfig - bot service config to handle the structured log event
 * * @param params.isCustomStream - whether the event is from a custom stream
 * * @param params.locale - locale to handle the structured log event
 * * @param params.conversationId - conversation id to handle the structured log event
 * * @param params.logger - logger to log the structured log data or errors when `structuredLog` event is dispatched
 * * @param params.structuredLogValidator - structured log validator to validate the structured log data
 */
export const useStructuredLog = <T extends IStructuredLogConfig & IStructuredLogBase>(params: IUseStructuredLogParams<T>) => {
  const structuredLogEventHandler = useCallback((event: Event) => {
    handleStructuredLogEvent<T>({ event, ...params });
  }, [params]);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    window.addEventListener(structuredLogEventName, structuredLogEventHandler);
    setIsReady(true);
    return () => {
      window.removeEventListener(structuredLogEventName, structuredLogEventHandler);
    };
  }, [structuredLogEventHandler]);

  return {
    isReady,
  };
};
